import {Component, OnInit, ViewChild} from '@angular/core';
import {MatMenuTrigger} from '@angular/material/menu';
import {MatSidenavModule} from '@angular/material/sidenav';
import {RouterModule} from '@angular/router';
import {NgClass, NgIf, NgOptimizedImage, NgStyle} from '@angular/common';
import {AdminService} from '../service/admin.service';
import {AdminEvent} from '../event/admin.event';
import {AdminEventTypeEnum} from '../enum/admin-event-type.enum';
import {MatToolbar} from '@angular/material/toolbar';
import {InformationAdminPartialLayoutComponent} from './partial/admin/information/information.component';
import {InformationEmployeePartialLayoutComponent} from './partial/employee/information/information.component';
import {LoadingIndicatorComponent} from '../component/loading-indicator/loading-indicator.component';
import {MatIcon} from '@angular/material/icon';
import {MatListItem, MatListItemIcon, MatListItemLine, MatListItemTitle, MatNavList} from '@angular/material/list';
import {environment} from "../../environment";

/**
 * @constant
 * @description This constant contains the expanded width of the menu.
 * @memberof LayoutComponent
 * @name MENU_WIDTH_EXPANDED
 * @type {number}
 */
const MENU_WIDTH_EXPANDED: number = 32;

/**
 * @constant
 * @description This constant contains the shrunk width of the menu.
 * @memberof LayoutComponent
 * @name MENU_WIDTH_SHRUNK
 * @type {number}
 */
const MENU_WIDTH_SHRUNK: number = 4;

/**
 *  @class
 *  @component
 *  @description This class maintains the main layout component for our application.
 *  @export
 *  @name LayoutComponent
 */
@Component({
  imports: [
    MatSidenavModule,
    NgIf,
    RouterModule,
    MatToolbar,
    NgOptimizedImage,
    InformationAdminPartialLayoutComponent,
    InformationEmployeePartialLayoutComponent,
    LoadingIndicatorComponent,
    MatIcon,
    MatListItem,
    MatListItemIcon,
    MatListItemLine,
    MatListItemTitle,
    MatNavList,
    NgClass,
    NgStyle
  ],
  selector: 'app-layout',
  standalone: true,
  styleUrl: './layout.component.scss',
  templateUrl: './layout.component.html'
})
export class LayoutComponent implements OnInit {
  /**
   * @default true
   * @description This property contains the hidden state of the menu.
   * @memberof LayoutComponent
   * @name LayoutComponent#menuHidden
   * @property
   * @protected
   * @type {boolean}
   */
  protected menuHidden: boolean = true;

  /**
   * @description This property contains the width of the menu.
   * @memberof LayoutComponent
   * @name LayoutComponent#menuWidth
   * @property
   * @protected
   * @type {number}
   */
  protected menuWidth: number = MENU_WIDTH_SHRUNK;

  /**
   * @description This property contains the trigger for our material menu
   * @memberof LayoutComponent
   * @name LayoutComponent#trigger
   * @property
   * @protected
   * @type {MatMenuTrigger}
   */
  @ViewChild(MatMenuTrigger)
  protected trigger!: MatMenuTrigger;

  /**
   * @description This property contains the year for the footer.
   * @memberof LayoutComponent
   * @name LayoutComponent#year
   * @property
   * @protected
   * @readonly
   * @type {number}
   */
  protected readonly year: number = new Date().getFullYear();

  /**
   * @constructor
   * @description This method instantiates the layout component.
   * @memberof LayoutComponent
   * @name LayoutComponent#constructor
   * @param {AdminService} adminService The injected instance of our administrator service provider.
   * @public
   */
  public constructor(
    /**
     * @description The injected instance of our administrator service provider.
     * @memberof LayoutComponent
     * @name LayoutComponent#adminService
     * @property
     * @private
     * @readonly
     * @type {AdminService}
     */
    private readonly adminService: AdminService
  ) {
  }

  /**
   * @description This method is called when the menu is expanded.
   * @memberof LayoutComponent
   * @method
   * @name LayoutComponent#expand
   * @protected
   * @returns {void}
   */
  protected expand(): void {

    // Toggle the menu width.
    this.menuWidth = MENU_WIDTH_EXPANDED;
  }

  /**
   * @description This method determines if the menu is open.
   * @memberof LayoutComponent
   * @method
   * @name LayoutComponent#open
   * @protected
   * @returns {boolean} The open state of the menu.
   */
  protected open(): boolean {

    // Return the open state of the menu.
    return this.menuWidth > MENU_WIDTH_SHRUNK;
  }

  /**
   * @description This method opens the integration documentation.
   * @memberof LayoutComponent
   * @method
   * @name LayoutComponent#openIntegrationDocumentation
   * @protected
   * @returns {void}
   */
  protected openIntegrationDocumentation(): void {

    // Open the integration documentation.
    window.open(environment.normalizedFullApiUrl('docs/index.html'), '_blank');
  }

  /**
   * @description This method is called when the menu is shrunk.
   * @memberof LayoutComponent
   * @method
   * @name LayoutComponent#shrink
   * @protected
   * @returns {void}
   */
  protected shrink(): void {

    // Toggle the menu width.
    this.menuWidth = MENU_WIDTH_SHRUNK;
  }

  /**
   * @description This method is called when the component is initialized.
   * @memberof LayoutComponent
   * @method
   * @name LayoutComponent#ngOnInit
   * @public
   * @returns {void}
   */
  public ngOnInit(): void {

    // Show the navigation menu if an authenticated administrator session is present.
    this.menuHidden = !this.adminService.online();

    // Subscribe to the admin service events to show the menu at the appropriate time.
    this.adminService.events.subscribe((e: AdminEvent): void => {

      // Check for a session-start or session-continued event then show the menu.
      if (e.type === AdminEventTypeEnum.SessionStart || e.type === AdminEventTypeEnum.SessionContinued) this.menuHidden = false;

      // Check for a session-end event the hide the menu.
      if (e.type === AdminEventTypeEnum.SessionEnd) this.menuHidden = true;
    });
  }
}
